import { useState, type MouseEvent } from 'react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@predoc/cow';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  EFAX_DISPLAY_STATUS_OPTIONS,
  type AnonymousRecordUploadStatus,
} from '../types';

interface EfaxStatusSelectorProps {
  value: string;
  onChange: (status: string) => void;
}

export const EfaxStatusSelector = ({
  value,
  onChange,
}: EfaxStatusSelectorProps) => {
  const [open, setOpen] = useState(false);

  const selectedStatus = EFAX_DISPLAY_STATUS_OPTIONS.find(
    (status) => status.value === value,
  );

  const handleSelect = (status: AnonymousRecordUploadStatus) => {
    setOpen(false);
    onChange(status);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'border-input bg-background h-9 w-full justify-between rounded-md border px-3 font-normal',
            selectedStatus && 'border-blue-400 bg-blue-50',
          )}
          aria-label="Select status"
        >
          <span
            className={cn(
              'truncate text-sm',
              selectedStatus ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {selectedStatus?.label ?? 'Any status'}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1"
        align="start"
      >
        {EFAX_DISPLAY_STATUS_OPTIONS.map((status) => {
          const isSelected = status.value === value;

          return (
            <Button
              key={status.value}
              type="button"
              variant="ghost"
              className="h-auto w-full justify-start gap-2 px-2 py-1.5"
              onMouseDown={(event: MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                handleSelect(status.value);
              }}
            >
              <span className="flex-1 text-left text-sm">{status.label}</span>
              {isSelected ? <Check className="size-4 shrink-0" /> : null}
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
